import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AdminApi,
  AdminLearningActivityItem,
  AdminSecurityWarningDetail,
  AdminSecurityWarningItem,
  AdminWatchlistItem,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

type MainTab = 'warnings' | 'watchlist'

const AdminWarningsPage = () => {
  const [mainTab, setMainTab] = useState<MainTab>('warnings')

  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [unresolvedCount, setUnresolvedCount] = useState(0)
  const [items, setItems] = useState<AdminSecurityWarningItem[]>([])
  const [loading, setLoading] = useState(false)

  const [watchPage, setWatchPage] = useState(1)
  const [watchTotalPages, setWatchTotalPages] = useState(1)
  const [watchItems, setWatchItems] = useState<AdminWatchlistItem[]>([])
  const [watchLoading, setWatchLoading] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addReason, setAddReason] = useState('CONCURRENT_STREAM_REVIEW')
  const [addNote, setAddNote] = useState('')

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [detail, setDetail] = useState<AdminSecurityWarningDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const loadWarnings = async () => {
    setLoading(true)
    try {
      const res = await AdminApi.getSecurityWarnings({ page, limit: 20 })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
      setUnresolvedCount(res.data.unresolvedCount)
    } catch {
      toast.error('Không tải được danh sách cảnh báo')
    } finally {
      setLoading(false)
    }
  }

  const loadWatchlist = async () => {
    setWatchLoading(true)
    try {
      const res = await AdminApi.listWatchlist({ page: watchPage, limit: 20 })
      setWatchItems(res.data.items)
      setWatchTotalPages(res.data.pagination.totalPages)
    } catch {
      toast.error('Không tải được watchlist')
    } finally {
      setWatchLoading(false)
    }
  }

  useEffect(() => {
    if (mainTab === 'warnings') void loadWarnings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, mainTab])

  useEffect(() => {
    if (mainTab === 'watchlist') void loadWatchlist()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchPage, mainTab])

  const openDetail = async (userId: number) => {
    setSelectedUserId(userId)
    setDetailLoading(true)
    try {
      const res = await AdminApi.getSecurityWarningDetail(userId)
      setDetail(res.data)
    } catch {
      toast.error('Không tải được chi tiết')
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const unlock = async (userId: number) => {
    setActionLoading(true)
    try {
      await AdminApi.unlockUser(userId)
      toast.success('Đã mở khóa tài khoản')
      await loadWarnings()
      if (mainTab === 'watchlist') await loadWatchlist()
      if (selectedUserId === userId) await openDetail(userId)
    } catch {
      toast.error('Mở khóa thất bại')
    } finally {
      setActionLoading(false)
    }
  }

  const lock = async (userId: number) => {
    setActionLoading(true)
    try {
      await AdminApi.lockUser(userId, 'ADMIN_MANUAL')
      toast.success('Đã khóa tài khoản')
      await loadWarnings()
      if (selectedUserId === userId) await openDetail(userId)
    } catch {
      toast.error('Khóa thất bại')
    } finally {
      setActionLoading(false)
    }
  }

  const revokeDevice = async (userId: number, deviceRowId: number) => {
    if (!window.confirm('Thu hồi thiết bị này để mở 1 slot đăng nhập mới?')) return
    setActionLoading(true)
    try {
      await AdminApi.revokeUserDevice(userId, deviceRowId)
      toast.success('Đã thu hồi thiết bị')
      await openDetail(userId)
      await loadWarnings()
    } catch {
      toast.error('Thu hồi thiết bị thất bại')
    } finally {
      setActionLoading(false)
    }
  }

  const revokeAllDevices = async (userId: number) => {
    if (
      !window.confirm(
        'Thu hồi TẤT CẢ thiết bị của học viên? Họ sẽ đăng nhập lại và đăng ký tối đa 4 máy mới.'
      )
    ) {
      return
    }
    setActionLoading(true)
    try {
      await AdminApi.revokeAllUserDevices(userId)
      toast.success('Đã thu hồi tất cả thiết bị')
      await openDetail(userId)
      await loadWarnings()
    } catch {
      toast.error('Thu hồi tất cả thiết bị thất bại')
    } finally {
      setActionLoading(false)
    }
  }

  const addWatchlistForUser = async (userId: number, reason?: string, note?: string) => {
    setActionLoading(true)
    try {
      await AdminApi.addToWatchlist(userId, {
        reason: reason || 'MANUAL',
        note: note || undefined,
      })
      toast.success('Đã thêm vào watchlist')
      await loadWatchlist()
      if (selectedUserId === userId) await openDetail(userId)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Thêm watchlist thất bại'
      toast.error(msg)
    } finally {
      setActionLoading(false)
    }
  }

  const removeWatchlistForUser = async (userId: number) => {
    setActionLoading(true)
    try {
      await AdminApi.removeFromWatchlist(userId)
      toast.success('Đã gỡ khỏi watchlist')
      await loadWatchlist()
      if (selectedUserId === userId) await openDetail(userId)
    } catch {
      toast.error('Gỡ watchlist thất bại')
    } finally {
      setActionLoading(false)
    }
  }

  const addWatchlistByEmail = async () => {
    const email = addEmail.trim()
    if (!email) {
      toast.error('Nhập email học viên')
      return
    }
    setActionLoading(true)
    try {
      const users = await AdminApi.listUsers({ q: email, limit: 10 })
      const match = users.data.items.find(
        (u) => u.email.toLowerCase() === email.toLowerCase()
      )
      if (!match) {
        toast.error('Không tìm thấy user với email này')
        return
      }
      await AdminApi.addToWatchlist(match.id, {
        reason: addReason.trim() || 'MANUAL',
        note: addNote.trim() || undefined,
      })
      toast.success(`Đã theo dõi ${match.email}`)
      setAddEmail('')
      setAddNote('')
      setMainTab('watchlist')
      await loadWatchlist()
      await openDetail(match.id)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Thêm watchlist thất bại'
      toast.error(msg)
    } finally {
      setActionLoading(false)
    }
  }

  const renderActivity = (a: AdminLearningActivityItem) => {
    const target =
      a.resourceType && a.resourceId
        ? `${a.resourceType}#${a.resourceId}`
        : a.contentId
          ? `content#${a.contentId}`
          : a.courseId
            ? `course#${a.courseId}`
            : '-'
    return (
      <li key={a.id} className="rounded border border-m2-outline-variant p-2">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{a.eventType}</span>
          <span className="text-m2-on-surface-variant">{formatDbDateTimeVi(a.createdAt)}</span>
        </div>
        <div className="mt-1 text-m2-on-surface-variant">
          {target}
          {a.ip ? ` · IP ${a.ip}` : ''}
          {a.deviceId ? ` · device ${a.deviceId.slice(0, 8)}…` : ''}
        </div>
        {a.userAgent && (
          <div className="mt-1 truncate text-[10px] text-m2-on-surface-variant" title={a.userAgent}>
            {a.userAgent}
          </div>
        )}
      </li>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-m2-on-surface">Cảnh báo bảo mật</h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            Cảnh báo tự động + watchlist theo dõi hoạt động học
          </p>
        </div>
        <div className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700">
          {unresolvedCount} cảnh báo chưa xử lý
        </div>
      </div>

      <div className="flex gap-2 border-b border-m2-outline-variant pb-2">
        <button
          type="button"
          className={`rounded px-3 py-1.5 text-sm ${
            mainTab === 'warnings'
              ? 'bg-m2-primary text-white'
              : 'bg-m2-surface-high text-m2-on-surface'
          }`}
          onClick={() => setMainTab('warnings')}
        >
          Cảnh báo
        </button>
        <button
          type="button"
          className={`rounded px-3 py-1.5 text-sm ${
            mainTab === 'watchlist'
              ? 'bg-m2-primary text-white'
              : 'bg-m2-surface-high text-m2-on-surface'
          }`}
          onClick={() => setMainTab('watchlist')}
        >
          Watchlist
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container lg:col-span-3">
          {mainTab === 'warnings' && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-m2-surface-low text-left text-xs uppercase text-m2-on-surface-variant">
                    <tr>
                      <th className="px-3 py-2">Học viên</th>
                      <th className="px-3 py-2">Trạng thái</th>
                      <th className="px-3 py-2">Sự kiện gần nhất</th>
                      <th className="px-3 py-2">Mở</th>
                      <th className="px-3 py-2">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-m2-on-surface-variant">
                          Đang tải...
                        </td>
                      </tr>
                    )}
                    {!loading && items.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-m2-on-surface-variant">
                          Không có cảnh báo
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      items.map((row) => (
                        <tr
                          key={row.id}
                          className={`border-t border-m2-outline-variant ${
                            selectedUserId === row.id ? 'bg-m2-primary/5' : ''
                          }`}
                        >
                          <td className="px-3 py-2">
                            <div className="font-medium text-m2-on-surface">{row.fullName}</div>
                            <div className="text-xs text-m2-on-surface-variant">{row.email}</div>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`rounded px-2 py-1 text-xs ${
                                row.status === 'BANNED'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {row.status === 'BANNED' ? 'Đã khóa' : row.status}
                            </span>
                            {row.lockReason && (
                              <div className="mt-1 text-[11px] text-m2-on-surface-variant">
                                {row.lockReason}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-m2-on-surface">
                            <div>{row.lastEventType || '-'}</div>
                            <div className="text-m2-on-surface-variant">
                              {row.lastEventAt ? formatDbDateTimeVi(row.lastEventAt) : '-'}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs font-medium text-m2-on-surface">
                            {row.openEventCount}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="rounded border border-m2-outline px-2 py-1 text-xs hover:bg-m2-surface-high"
                                onClick={() => void openDetail(row.id)}
                              >
                                Chi tiết
                              </button>
                              <button
                                type="button"
                                disabled={actionLoading}
                                className="rounded border border-amber-400 px-2 py-1 text-xs text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                                onClick={() =>
                                  void addWatchlistForUser(row.id, row.lockReason || 'WARNING_FOLLOWUP')
                                }
                              >
                                Watchlist
                              </button>
                              {row.status === 'BANNED' ? (
                                <button
                                  type="button"
                                  disabled={actionLoading}
                                  className="rounded bg-m2-primary px-2 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
                                  onClick={() => void unlock(row.id)}
                                >
                                  Mở khóa
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={actionLoading}
                                  className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
                                  onClick={() => void lock(row.id)}
                                >
                                  Khóa
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 border-t border-m2-outline-variant px-3 py-2">
                  <button
                    type="button"
                    className="rounded border border-m2-outline px-2 py-1 text-xs disabled:opacity-40"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Trước
                  </button>
                  <span className="text-xs text-m2-on-surface-variant">
                    {page}/{totalPages}
                  </span>
                  <button
                    type="button"
                    className="rounded border border-m2-outline px-2 py-1 text-xs disabled:opacity-40"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Sau
                  </button>
                </div>
              )}
            </>
          )}

          {mainTab === 'watchlist' && (
            <>
              <div className="space-y-2 border-b border-m2-outline-variant p-3">
                <div className="text-xs font-semibold uppercase text-m2-on-surface-variant">
                  Thêm vào watchlist
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    className="min-w-[200px] flex-1 rounded border border-m2-outline px-2 py-1.5 text-sm"
                    placeholder="Email học viên"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                  />
                  <input
                    className="w-48 rounded border border-m2-outline px-2 py-1.5 text-sm"
                    placeholder="Reason"
                    value={addReason}
                    onChange={(e) => setAddReason(e.target.value)}
                  />
                  <button
                    type="button"
                    disabled={actionLoading}
                    className="rounded bg-m2-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
                    onClick={() => void addWatchlistByEmail()}
                  >
                    Thêm
                  </button>
                </div>
                <input
                  className="w-full rounded border border-m2-outline px-2 py-1.5 text-sm"
                  placeholder="Ghi chú (tuỳ chọn)"
                  value={addNote}
                  onChange={(e) => setAddNote(e.target.value)}
                />
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-m2-surface-low text-left text-xs uppercase text-m2-on-surface-variant">
                    <tr>
                      <th className="px-3 py-2">Học viên</th>
                      <th className="px-3 py-2">Lý do</th>
                      <th className="px-3 py-2">Hoạt động gần nhất</th>
                      <th className="px-3 py-2">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {watchLoading && (
                      <tr>
                        <td colSpan={4} className="px-3 py-4 text-center text-m2-on-surface-variant">
                          Đang tải...
                        </td>
                      </tr>
                    )}
                    {!watchLoading && watchItems.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-3 py-4 text-center text-m2-on-surface-variant">
                          Watchlist trống
                        </td>
                      </tr>
                    )}
                    {!watchLoading &&
                      watchItems.map((row) => (
                        <tr
                          key={row.id}
                          className={`border-t border-m2-outline-variant ${
                            selectedUserId === row.userId ? 'bg-m2-primary/5' : ''
                          }`}
                        >
                          <td className="px-3 py-2">
                            <div className="font-medium text-m2-on-surface">{row.fullName}</div>
                            <div className="text-xs text-m2-on-surface-variant">{row.email}</div>
                            <div className="mt-1 text-[11px] text-m2-on-surface-variant">
                              {row.status}
                              {row.lockReason ? ` · ${row.lockReason}` : ''}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <div>{row.reason || '-'}</div>
                            {row.note && (
                              <div className="mt-1 text-m2-on-surface-variant">{row.note}</div>
                            )}
                            <div className="mt-1 text-m2-on-surface-variant">
                              {formatDbDateTimeVi(row.createdAt)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-m2-on-surface-variant">
                            {row.lastActivityAt ? formatDbDateTimeVi(row.lastActivityAt) : 'Chưa có'}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="rounded border border-m2-outline px-2 py-1 text-xs hover:bg-m2-surface-high"
                                onClick={() => void openDetail(row.userId)}
                              >
                                Chi tiết
                              </button>
                              <button
                                type="button"
                                disabled={actionLoading}
                                className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 disabled:opacity-50"
                                onClick={() => void removeWatchlistForUser(row.userId)}
                              >
                                Gỡ
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {watchTotalPages > 1 && (
                <div className="flex items-center justify-end gap-2 border-t border-m2-outline-variant px-3 py-2">
                  <button
                    type="button"
                    className="rounded border border-m2-outline px-2 py-1 text-xs disabled:opacity-40"
                    disabled={watchPage <= 1}
                    onClick={() => setWatchPage((p) => Math.max(1, p - 1))}
                  >
                    Trước
                  </button>
                  <span className="text-xs text-m2-on-surface-variant">
                    {watchPage}/{watchTotalPages}
                  </span>
                  <button
                    type="button"
                    className="rounded border border-m2-outline px-2 py-1 text-xs disabled:opacity-40"
                    disabled={watchPage >= watchTotalPages}
                    onClick={() => setWatchPage((p) => Math.min(watchTotalPages, p + 1))}
                  >
                    Sau
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4 lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-m2-on-surface-variant">
            Chi tiết
          </h2>
          {!selectedUserId && (
            <p className="text-sm text-m2-on-surface-variant">Chọn một học viên để xem log.</p>
          )}
          {detailLoading && <p className="text-sm text-m2-on-surface-variant">Đang tải...</p>}
          {!detailLoading && detail && (
            <div className="space-y-4">
              <div>
                <div className="font-medium text-m2-on-surface">{detail.user.fullName}</div>
                <div className="text-xs text-m2-on-surface-variant">{detail.user.email}</div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span>
                    Trạng thái: <strong>{detail.user.status}</strong>
                    {detail.user.lockReason ? ` · ${detail.user.lockReason}` : ''}
                  </span>
                  {detail.watchlisted ? (
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-800">
                      Đang theo dõi
                    </span>
                  ) : null}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {detail.user.status === 'BANNED' && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      className="rounded bg-m2-primary px-3 py-1.5 text-xs text-white disabled:opacity-50"
                      onClick={() => void unlock(detail.user.id)}
                    >
                      Mở khóa tài khoản
                    </button>
                  )}
                  {detail.watchlisted ? (
                    <button
                      type="button"
                      disabled={actionLoading}
                      className="rounded border border-red-300 px-3 py-1.5 text-xs text-red-700 disabled:opacity-50"
                      onClick={() => void removeWatchlistForUser(detail.user.id)}
                    >
                      Gỡ watchlist
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={actionLoading}
                      className="rounded border border-amber-400 px-3 py-1.5 text-xs text-amber-800 disabled:opacity-50"
                      onClick={() =>
                        void addWatchlistForUser(
                          detail.user.id,
                          detail.user.lockReason || 'MANUAL'
                        )
                      }
                    >
                      Thêm watchlist
                    </button>
                  )}
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase text-m2-on-surface-variant">
                    Thiết bị (
                    {detail.devices.filter((d) => !d.revokedAt).length}/4 active ·{' '}
                    {detail.devices.length}/8 tổng)
                  </h3>
                  {detail.devices.some((d) => !d.revokedAt) && (
                    <button
                      type="button"
                      disabled={actionLoading}
                      className="rounded border border-red-300 px-2 py-1 text-[11px] text-red-700 disabled:opacity-50"
                      onClick={() => void revokeAllDevices(detail.user.id)}
                    >
                      Thu hồi tất cả
                    </button>
                  )}
                </div>
                <ul className="max-h-52 space-y-2 overflow-y-auto text-xs">
                  {detail.devices.map((d) => {
                    const revoked = Boolean(d.revokedAt)
                    return (
                      <li
                        key={d.id}
                        className={`rounded border p-2 ${
                          revoked
                            ? 'border-m2-outline-variant/60 opacity-60'
                            : 'border-m2-outline-variant'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-medium">
                              {d.deviceLabel || 'Device'} · {d.deviceId.slice(0, 8)}…
                              {revoked ? ' (đã thu hồi)' : ''}
                            </div>
                            <div className="text-m2-on-surface-variant">
                              IP: {d.lastIp || '-'} · {formatDbDateTimeVi(d.lastSeenAt)}
                            </div>
                          </div>
                          {!revoked && (
                            <button
                              type="button"
                              disabled={actionLoading}
                              className="shrink-0 rounded bg-m2-surface-high px-2 py-1 text-[11px] text-m2-on-surface disabled:opacity-50"
                              onClick={() => void revokeDevice(detail.user.id, d.id)}
                            >
                              Thu hồi
                            </button>
                          )}
                        </div>
                      </li>
                    )
                  })}
                  {detail.devices.length === 0 && (
                    <li className="text-m2-on-surface-variant">Chưa có thiết bị</li>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-m2-on-surface-variant">
                  Hoạt động học ({detail.learningActivities?.length || 0})
                </h3>
                {!detail.watchlisted && (
                  <p className="mb-2 text-[11px] text-m2-on-surface-variant">
                    Chỉ ghi log mới khi học viên nằm trong watchlist. Lịch sử cũ (nếu có) vẫn hiện
                    bên dưới.
                  </p>
                )}
                <ul className="max-h-56 space-y-2 overflow-y-auto text-xs">
                  {(detail.learningActivities || []).map(renderActivity)}
                  {(detail.learningActivities || []).length === 0 && (
                    <li className="text-m2-on-surface-variant">Chưa có hoạt động học được ghi</li>
                  )}
                </ul>
              </div>

              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase text-m2-on-surface-variant">
                  Timeline sự kiện bảo mật
                </h3>
                <ul className="max-h-72 space-y-2 overflow-y-auto text-xs">
                  {detail.events.map((e) => (
                    <li key={e.id} className="rounded border border-m2-outline-variant p-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{e.eventType}</span>
                        <span
                          className={`rounded px-1.5 py-0.5 ${
                            e.severity === 'critical'
                              ? 'bg-red-100 text-red-700'
                              : e.severity === 'warning'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-m2-surface-high text-m2-on-surface-variant'
                          }`}
                        >
                          {e.severity}
                        </span>
                      </div>
                      <div className="mt-1 text-m2-on-surface-variant">
                        {formatDbDateTimeVi(e.createdAt)}
                        {e.resolvedAt ? ' · đã xử lý' : ''}
                      </div>
                      {e.payload && (
                        <pre className="mt-1 max-h-24 overflow-auto rounded bg-m2-surface-low p-1 text-[10px] text-m2-on-surface-variant">
                          {JSON.stringify(e.payload, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminWarningsPage
